import React, { useEffect, useState } from "react";
import { Copy, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { generatePassword } from "@/lib/passwords";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const newTemp = () => generatePassword({ length: 16 });

export default function ResetPasswordDialog({ member, onClose }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (member) setPassword(newTemp()); }, [member]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success("Temporary password copied");
    } catch {
      toast.error("Unable to copy");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Temporary password must be at least 8 characters");
    setSaving(true);
    try {
      await api.post(`/admin/members/${member.id}/reset-password`, { password });
      toast.success(`Password reset for ${member.name || member.email}. They'll choose a new one at sign-in.`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-xl">Reset password for {member?.name || member?.email}</DialogTitle>
          <DialogDescription>
            Share this temporary password with them securely. They'll be asked to set their own the next time they sign in. Their saved items are not affected.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.05em] text-[color:var(--vault-text-tertiary)]">Temporary password</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl bg-[#F1F5FA] border-[color:var(--vault-border)] font-mono"
                data-testid="reset-password-input"
              />
              <Button type="button" variant="outline" size="icon" className="rounded-xl shrink-0" onClick={() => setPassword(newTemp())} title="Generate new" data-testid="reset-password-regenerate">
                <RefreshCw size={15} />
              </Button>
              <Button type="button" variant="outline" size="icon" className="rounded-xl shrink-0" onClick={copy} title="Copy" data-testid="reset-password-copy">
                <Copy size={15} />
              </Button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={onClose} data-testid="reset-password-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-xl bg-[color:var(--vault-brand)] hover:bg-[color:var(--vault-brand-hover)] text-white" data-testid="reset-password-confirm">
              {saving ? "Resetting…" : "Reset password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
